import { useMemo, useState } from "react";
import { Languages } from "lucide-react";

import { Badge } from "@/components/shadcn/badge";
import { Button } from "@/components/shadcn/button";
import { RichTextValue } from "@/components/Form/RichTextField";
import { AgentLanguageInput, NoxusTipTapDocumentInput } from "@/generated";
import { LanguageOption } from "@/types/api/spotflow/spotflow-config";

import { ExpandableTextarea } from "./ExpandableTextarea";
import { LanguagesDialog } from "./LanguagesDialog";
import { LanguageChip, describeLanguage } from "./languageUtils";

const manageLabel = (languages: AgentLanguageInput[]) =>
  languages.length > 1
    ? `Manage ${languages.length} languages`
    : "Manage languages";

export const LanguagesField = ({
  languages,
  onLanguagesChange,
  defaultLanguage,
  onDefaultLanguageChange,
  isDisabled,
  introPlaceholder,
  options,
  addableCodes,
}: {
  languages: AgentLanguageInput[] | undefined;
  onLanguagesChange: (languages: AgentLanguageInput[]) => void;
  defaultLanguage: string | null | undefined;
  onDefaultLanguageChange: (code: string | null) => void;
  isDisabled?: boolean;
  introPlaceholder?: string | null;
  options: LanguageOption[];
  addableCodes: string[];
}) => {
  const value = useMemo(() => languages ?? [], [languages]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const optionByValue = useMemo(
    () => new Map(options.map((option) => [option.value, option])),
    [options]
  );

  // The backend anchors an out-of-list default onto the first entry; mirror
  // that so the card never points at a language that was removed.
  const effectiveDefault =
    defaultLanguage &&
    value.some((language) => language.code === defaultLanguage)
      ? defaultLanguage
      : (value[0]?.code ?? null);

  const defaultEntry = value.find(
    (language) => language.code === effectiveDefault
  );
  const defaultDescriptor = defaultEntry
    ? describeLanguage(defaultEntry, optionByValue)
    : null;

  const setDefaultIntro = (intro: RichTextValue) => {
    if (isDisabled || !defaultEntry) return;
    onLanguagesChange(
      value.map((language) =>
        language.code === defaultEntry.code
          ? {
              ...language,
              introductory_message: [intro as NoxusTipTapDocumentInput],
            }
          : language
      )
    );
  };

  return (
    <div className="flex flex-col gap-2">
      <div>
        <p className="text-sm font-medium leading-none text-foreground">
          Welcome message
        </p>
        <p className="mt-1.5 text-xs font-regular text-muted-foreground">
          The message the agent uses to start the chat.
        </p>
      </div>

      <div className="overflow-hidden rounded-lg border border-border">
        {defaultEntry && defaultDescriptor ? (
          <>
            <div className="flex items-center gap-2 p-2">
              <LanguageChip tag={defaultDescriptor.tag} />
              <p className="min-w-0 flex-1 truncate text-sm">
                {defaultDescriptor.label}
              </p>
              <Badge variant="secondary">Default</Badge>
            </div>
            <ExpandableTextarea
              defaultExpanded
              isFlush
              value={defaultEntry.introductory_message?.[0] ?? ""}
              onChange={setDefaultIntro}
              isDisabled={isDisabled}
              label="Welcome message"
              placeholder={
                introPlaceholder ?? "Hello, how can I help you today?"
              }
            />
          </>
        ) : (
          <p className="px-3 py-6 text-center text-sm text-muted-foreground">
            No languages configured yet.
          </p>
        )}

        <div className="flex items-center justify-end border-t border-border bg-muted/40 px-2 py-1.5">
          <Button
            variant="ghost"
            size="xs"
            isDisabled={isDisabled}
            onClick={() => setIsDialogOpen(true)}
          >
            <Languages size={14} />
            {manageLabel(value)}
          </Button>
        </div>
      </div>

      <LanguagesDialog
        isOpen={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        languages={value}
        onLanguagesChange={onLanguagesChange}
        defaultLanguage={effectiveDefault}
        onDefaultLanguageChange={onDefaultLanguageChange}
        options={options}
        addableCodes={addableCodes}
        introPlaceholder={introPlaceholder}
        isDisabled={isDisabled}
      />
    </div>
  );
};
